import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '../../components/AdminLayout';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  FaUsers, FaBox, FaShoppingCart, FaEuroSign, 
  FaStore, FaStar, FaFileAlt, FaClipboardCheck 
} from 'react-icons/fa';

const COLORS = ['#0A2540', '#635BFF', '#FF9800', '#F44336', '#9C27B0', '#2196F3'];

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [productStatus, setProductStatus] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [quoteStatus, setQuoteStatus] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [
        statsRes,
        userGrowthRes,
        revenueTrendRes,
        productStatusRes,
        topCategoriesRes,
        recentActivitiesRes,
        quoteStatusRes
      ] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`),
        axios.get(`${API}/admin/dashboard/user-growth`),
        axios.get(`${API}/admin/dashboard/revenue-trend`),
        axios.get(`${API}/admin/dashboard/product-status`),
        axios.get(`${API}/admin/dashboard/top-categories`),
        axios.get(`${API}/admin/dashboard/recent-activities`),
        axios.get(`${API}/admin/dashboard/quote-status-distribution`)
      ]);

      setStats(statsRes.data);
      setUserGrowth(userGrowthRes.data);
      setRevenueTrend(revenueTrendRes.data);
      setProductStatus(productStatusRes.data);
      setTopCategories(topCategoriesRes.data);
      setRecentActivities(recentActivitiesRes.data);
      setQuoteStatus(quoteStatusRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
          <div className="text-xl text-gray-600">Chargement du tableau de bord...</div>
        </div>
      </AdminLayout>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold" style={{ color }}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className="p-4 rounded-full" style={{ backgroundColor: `${color}20` }}>
            <Icon className="text-3xl" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-dashboard">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
              📊 Tableau de Bord Administrateur
            </h1>
            <p className="text-gray-600">Vue d'ensemble complète de votre plateforme</p>
          </div>

          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard 
              title="Utilisateurs Total"
              value={stats?.users?.total || 0}
              icon={FaUsers}
              color="#0A2540"
              subtitle={`${stats?.users?.suppliers || 0} fournisseurs, ${stats?.users?.admins || 0} admins`}
            />
            <StatCard 
              title="Produits Total"
              value={stats?.products?.total || 0}
              icon={FaBox}
              color="#635BFF"
              subtitle={`${stats?.products?.pending || 0} en attente`}
            />
            <StatCard 
              title="Commandes Total"
              value={stats?.orders?.total || 0}
              icon={FaShoppingCart}
              color="#FF9800"
            />
            <StatCard 
              title="Revenus Total"
              value={`${(stats?.revenue?.total || 0).toFixed(2)} €`}
              icon={FaEuroSign}
              color="#F44336"
            />
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard 
              title="Boutiques"
              value={stats?.shops?.total || 0}
              icon={FaStore}
              color="#9C27B0"
            />
            <StatCard 
              title="Avis"
              value={stats?.reviews?.total || 0}
              icon={FaStar}
              color="#FF9800"
            />
            <StatCard 
              title="Demandes de Devis"
              value={stats?.quotes?.total || 0}
              icon={FaFileAlt}
              color="#2196F3"
              subtitle={`${stats?.quotes?.pending || 0} en attente`}
            />
            <StatCard 
              title="Demandes Publiques"
              value={stats?.requests?.total || 0}
              icon={FaClipboardCheck}
              color="#635BFF"
              subtitle={`${stats?.requests?.pending || 0} en attente`}
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* User Growth Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540]">
                  📈 Croissance des Utilisateurs (30 derniers jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#0A2540" 
                      strokeWidth={2}
                      name="Nouveaux utilisateurs"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540]">
                  💰 Tendance des Revenus (12 derniers mois)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="revenue" 
                      fill="#635BFF" 
                      name="Revenus (€)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Product Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540]">
                  📦 Statut des Produits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={productStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, count }) => `${status}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {productStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540]">
                  🏆 Top 5 Catégories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topCategories} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="category" 
                      type="category" 
                      width={100}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0A2540" name="Produits" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Quote Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540]">
                  📋 Statut des Devis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={quoteStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, count }) => `${status}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {quoteStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#0A2540]">
                🔔 Activités Récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentActivities.map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="mt-1">
                      {activity.type === 'user' && <FaUsers className="text-[#0A2540]" />}
                      {activity.type === 'product' && <FaBox className="text-[#635BFF]" />}
                      {activity.type === 'order' && <FaShoppingCart className="text-[#FF9800]" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.timestamp).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
